export type FramePacingMode = "auto" | "quality" | "low";

const LOW_FRAME_INTERVAL_MS = 1_000 / 30;
const TIMING_EPSILON_MS = 1e-7;

/**
 * Decides whether a requestAnimationFrame callback should produce a canvas
 * frame. Simulation can continue on every callback independently.
 */
export class FramePacer {
  private lastTimestampMs: number | null = null;
  private accumulatedMs = 0;
  private previousMode: FramePacingMode | null = null;

  shouldRender(timestampMs: number, mode: FramePacingMode): boolean {
    if (mode !== this.previousMode) this.resetTiming(mode);

    if (mode !== "low") {
      this.lastTimestampMs = timestampMs;
      return true;
    }

    if (this.lastTimestampMs === null || !Number.isFinite(timestampMs)) {
      this.lastTimestampMs = timestampMs;
      return true;
    }

    const elapsedMs = timestampMs - this.lastTimestampMs;
    this.lastTimestampMs = timestampMs;
    if (elapsedMs < 0 || !Number.isFinite(elapsedMs)) {
      this.accumulatedMs = 0;
      return true;
    }

    this.accumulatedMs += elapsedMs;
    if (this.accumulatedMs + TIMING_EPSILON_MS < LOW_FRAME_INTERVAL_MS)
      return false;

    this.accumulatedMs =
      this.accumulatedMs < LOW_FRAME_INTERVAL_MS
        ? 0
        : this.accumulatedMs % LOW_FRAME_INTERVAL_MS;
    return true;
  }

  reset(): void {
    this.lastTimestampMs = null;
    this.accumulatedMs = 0;
    this.previousMode = null;
  }

  private resetTiming(mode: FramePacingMode): void {
    this.lastTimestampMs = null;
    this.accumulatedMs = 0;
    this.previousMode = mode;
  }
}
